import { useMemo, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { BucketSize, PeriodDuration } from "@/lib/analytics/types";
import { bucketAdverb } from "@/lib/analytics/buckets";
import { formatMs } from "@/lib/utils/format-duration";

interface DurationTrendProps {
  rows: PeriodDuration[];
  bucket: BucketSize;
  /** Period B rows (shifted to align index-wise with period A). */
  compareRows?: PeriodDuration[];
}

export function DurationTrend({ rows, bucket, compareRows }: DurationTrendProps) {
  const [showP50, setShowP50] = useState(true);
  const [showP95, setShowP95] = useState(true);

  const hasCompare = compareRows != null && compareRows.length > 0;

  const data = useMemo(() => {
    return rows.map((r, i) => {
      const entry: Record<string, number | string> = {
        period: r.period,
        p50: Math.round(r.p50Ms / 1000),
        p95: Math.round(r.p95Ms / 1000),
      };
      if (compareRows) {
        const cmp = compareRows[i];
        entry["p50Prev"] = cmp ? Math.round(cmp.p50Ms / 1000) : 0;
        entry["p95Prev"] = cmp ? Math.round(cmp.p95Ms / 1000) : 0;
        entry["_comparePeriod"] = cmp?.period ?? "";
      }
      return entry;
    });
  }, [rows, compareRows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Duration trend</CardTitle>
        <CardDescription>
          {bucketAdverb(bucket).charAt(0).toUpperCase() + bucketAdverb(bucket).slice(1)} p50 / p95 (wall-clock seconds)
          {hasCompare ? " — dotted = previous period" : ""}
        </CardDescription>
      </CardHeader>
      <CardContent className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="period" stroke="var(--muted-foreground)" fontSize={12} />
            <YAxis
              tickFormatter={(v) => `${v}s`}
              stroke="var(--muted-foreground)"
              fontSize={12}
            />
            <Tooltip
              contentStyle={{
                background: "var(--popover)",
                border: "1px solid var(--border)",
                borderRadius: "0.375rem",
                fontSize: "0.75rem",
              }}
              formatter={(v, name, props) => {
                const cmpPeriod = (props.payload as Record<string, unknown>)["_comparePeriod"];
                const periodLabel = cmpPeriod ? ` (${String(cmpPeriod)})` : "";
                if (name === "p50Prev") return [formatMs(Number(v ?? 0) * 1000), `p50 prev${periodLabel}`];
                if (name === "p95Prev") return [formatMs(Number(v ?? 0) * 1000), `p95 prev${periodLabel}`];
                return [formatMs(Number(v ?? 0) * 1000), name];
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: "0.75rem", cursor: "pointer" }}
              onClick={(e) => {
                if (e.dataKey === "p50" || e.dataKey === "p50Prev") setShowP50((p) => !p);
                if (e.dataKey === "p95" || e.dataKey === "p95Prev") setShowP95((p) => !p);
              }}
            />
            {showP50 ? (
              <Line
                type="monotone"
                dataKey="p50"
                stroke="var(--chart-1)"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="p50"
                isAnimationActive={false}
              />
            ) : null}
            {showP95 ? (
              <Line
                type="monotone"
                dataKey="p95"
                stroke="var(--chart-4)"
                strokeWidth={2}
                dot={{ r: 3 }}
                name="p95"
                isAnimationActive={false}
              />
            ) : null}
            {hasCompare && showP50 ? (
              <Line
                type="monotone"
                dataKey="p50Prev"
                stroke="var(--chart-1)"
                strokeWidth={2}
                strokeDasharray="4 2"
                strokeOpacity={0.6}
                dot={false}
                name="p50Prev"
                isAnimationActive={false}
              />
            ) : null}
            {hasCompare && showP95 ? (
              <Line
                type="monotone"
                dataKey="p95Prev"
                stroke="var(--chart-4)"
                strokeWidth={2}
                strokeDasharray="4 2"
                strokeOpacity={0.6}
                dot={false}
                name="p95Prev"
                isAnimationActive={false}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
